/**
 * 3D voice orb: high-resolution icosphere displaced by simplex noise in the
 * vertex shader, fresnel rim + halo. The deformation amplitude is driven by
 * the real audio volume (setLevel).
 */
import {
  BackSide,
  Color,
  IcosahedronGeometry,
  Mesh,
  PerspectiveCamera,
  Scene,
  ShaderMaterial,
  WebGLRenderer,
} from "three";
import {
  haloFragmentShader,
  haloVertexShader,
  orbFragmentShader,
  orbVertexShader,
} from "./orb.shaders";

export type OrbMode = "idle" | "listening" | "speaking";

type CreateOrbParams = { canvas: HTMLCanvasElement };

export type Orb = {
  setMode: (params: { mode: OrbMode }) => void;
  setLevel: (params: { level: number }) => void;
};

const AMPLITUDE = {
  idle: { base: 0.06, gain: 0 },
  listening: { base: 0.07, gain: 0.12 },
  speaking: { base: 0.1, gain: 0.55 },
} as const;

export const createOrb = ({ canvas }: CreateOrbParams): Orb => {
  const renderer = new WebGLRenderer({ canvas, alpha: true, antialias: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

  const scene = new Scene();
  const camera = new PerspectiveCamera(45, 1, 0.1, 20);
  // Far enough so the halo is never clipped by the canvas edges
  camera.position.z = 4.3;

  const orbMaterial = new ShaderMaterial({
    vertexShader: orbVertexShader,
    fragmentShader: orbFragmentShader,
    uniforms: {
      uTime: { value: 0 },
      uAmp: { value: AMPLITUDE.idle.base },
      uGlow: { value: 0 },
      uColorDeep: { value: new Color("#015b66") },
      uColorLight: { value: new Color("#2ec9d8") },
    },
  });
  const orbMesh = new Mesh(new IcosahedronGeometry(1, 96), orbMaterial);
  scene.add(orbMesh);

  // Classic alpha blending: additive vanishes on a light background
  const haloMaterial = new ShaderMaterial({
    vertexShader: haloVertexShader,
    fragmentShader: haloFragmentShader,
    uniforms: { uGlow: { value: 0 } },
    side: BackSide,
    transparent: true,
    depthWrite: false,
  });
  const haloMesh = new Mesh(new IcosahedronGeometry(1.35, 24), haloMaterial);
  scene.add(haloMesh);

  let mode: OrbMode = "idle";
  let targetLevel = 0;
  let smoothedLevel = 0;

  const resize = () => {
    const size = canvas.clientWidth;
    renderer.setSize(size, size, false);
  };
  window.addEventListener("resize", resize);
  resize();

  renderer.setAnimationLoop((elapsedMs: number) => {
    const time = elapsedMs / 1000;
    // Fast attack, soft release — the orb follows the voice without jitter
    const ease = targetLevel > smoothedLevel ? 0.4 : 0.07;
    smoothedLevel += (targetLevel - smoothedLevel) * ease;

    const { base, gain } = AMPLITUDE[mode];
    orbMaterial.uniforms.uTime.value = time;
    orbMaterial.uniforms.uAmp.value = base + smoothedLevel * gain;
    orbMaterial.uniforms.uGlow.value = smoothedLevel;
    haloMaterial.uniforms.uGlow.value = smoothedLevel;

    const pulse = 1 + smoothedLevel * (mode === "speaking" ? 0.14 : 0.04);
    orbMesh.scale.setScalar(pulse);
    haloMesh.scale.setScalar(pulse);

    orbMesh.rotation.y = time * 0.12;
    orbMesh.rotation.x = Math.sin(time * 0.2) * 0.15;

    renderer.render(scene, camera);
  });

  return {
    setMode: ({ mode: nextMode }) => {
      mode = nextMode;
    },
    setLevel: ({ level }) => {
      targetLevel = Math.min(1, Math.max(0, level));
    },
  };
};
